import Box from "@mui/material/Box";
import { DataGrid, GridToolbarFilterButton } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import LinkIcon from '@mui/icons-material/Link';
import { getSurveyPartsBySupplier, getSurveyPartsHistoryBySupplier, deleteSurveyByPartAttachment } from "../../services/surveypart.service";
import { SURVEY_RESPONSE_STATUSES, PARTS_TABLE_ACCESSOR_KEYS, SAMPLE_DATA_CODE, FILE_DELETE_RESPONSE, FILE_DELETE_ATTACHMENT, FILE_EXTENSIONS_TO_EXCLUDE, EWDC, PART_SUPPLIER_NUMBER_COLUMN, PART_FIRST_NAME_COLUMN, PART_LAST_NAME_COLUMN, PART_RACF_COLUMN, addCol1, addCol1Accessor, addCol2Accessor } from "../../utils/surveyLiterals";
import { CircleProgressIndicator } from "../CircleProgressIndicator";
import { IconButton, SelectField, Option, Grid, Link, Typography, Tooltip } from "@deere/fuel-react";
import { TextField, Checkbox } from "@deere/fuel-react";
import { ReactComponent as AddIcon } from "@deere/ux.brand-foundations/icons/add_circle.svg";
import { ReactComponent as AttachmentIcon } from "@deere/ux.brand-foundations/icons/download.svg";
import { ReactComponent as DeleteIcon } from "@deere/ux.brand-foundations/icons/delete.svg";
import { ReactComponent as UploadIcon } from "@deere/ux.brand-foundations/icons/upload.svg";
import { QUESTION_TYPES, POPUP_QUESTIONS_TITLES } from "../../utils/constants";
import ModalCellPopup from "./ModalCellPopup";
import DownloadPartsTable from "./part-table-download/DownloadPartsTable";
import UploadPartsTable from "./part-table-upload/UploadPartsTable";
import { getTranslation, getTranslationHelpText } from "../../utils/utils";
import { getBoundary, getCountry } from "../../services/external.service";
import { useDispatch, useSelector } from 'react-redux';
import { updateCountryList, updateStateByQuesIdList } from "../../redux/action/surveyPartAction";
import { getSurveyByPartAttachmentUrl, uploadSurveyByPartAttachments } from "../../services/surveyHelpDoc.service";
import { Popover, OverlayTrigger, List, ListItem } from '@deere/ux.uxframe-react';
import { ERR_FILE_DELETION, ERR_FILE_NOT_AVAILABLE, ERR_UNEXPECTED_ERROR_FILE, ERR_UNSUPPORTED_FILE } from "../../utils/errorLiterals";
import ModalMultiCountry from "./ModalMultiCountry";
import { Select, MenuItem } from "@mui/material";

export const PartsTable = ({
  setDependentQuestions,
  dependentQuestions,
  campaign,
  questions,
  suppliers,
  updatePartsArray,
  selectedResponseId,
  selectionModel,
  setSelectionModel,
  transactionParts,
  setTransactionParts,
  partErrorIds,
  setPartErrorIds,
  isAuthMethodOkta,
  token,
  literals,
  code,
  saveButtonClickFlag,
  setSaveButtonClickFlag,
  multiplePartSections,
  sectionCode,
  defaultColumns,
  assignments
}) => {
  const [data, setData] = useState(undefined);
  const [showPopupCellModal, setShowPopupCellModal] = useState(false);
  const [rowsToDisable, setRowsToDisable] = useState([]);
  const [popupCellParams, setPopupCellParams] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [partsError, setPartsError] = useState({show: false, message: ""});
  const [showModalMultiCountry, setShowModalMultiCountry] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [activeDataForUpload, setActiveDataForUpload] = useState([]);
  const [inactiveDataForWarning, setInactiveDataForWarning] = useState([]);
  const { t } = useTranslation();
  const questionIds = questions ? questions.map((que) => "que_" + que.id) : [];
  const style = { width: "100%", "& .disable-row-status": { color: "#b3b3b3" } };
  const sampleData = literals.filter(literal => literal.code.split('_')[0] === SAMPLE_DATA_CODE);
  const dispatch = useDispatch();
  const fileInputRef = useRef([]);
  const countryList = useSelector((state) => state.surveyPartReducer.countryList);
  const countryListWithUnknown = [
    ...(countryList || []),
    { iso2CountryCode: "UN", name: "Unknown" },
  ];
  const stateList = useSelector(state => state.surveyPartReducer.stateList);
  /******************************************Setting Columns for Data Table******************************************/
  let columns = [];
  const fixedColumns = code === EWDC ? useMemo(() => [
    { field: PARTS_TABLE_ACCESSOR_KEYS.SUPPLIER_NUM, headerName: PART_SUPPLIER_NUMBER_COLUMN, description: t("parts_tbl_column_header_1"), minWidth: 100, flex: 1 },
    { field: PARTS_TABLE_ACCESSOR_KEYS.DEERE_PART_NUM, headerName: PART_FIRST_NAME_COLUMN, description: t("parts_tbl_column_header_2"), minWidth: 100, flex: 1 },
    { field: PARTS_TABLE_ACCESSOR_KEYS.DEERE_PART_DESC, headerName: PART_LAST_NAME_COLUMN, description: t("parts_tbl_column_header_3"), minWidth: 100, flex: 1 },
    { field: PARTS_TABLE_ACCESSOR_KEYS.HS_CODE, headerName: PART_RACF_COLUMN, description: t("parts_tbl_column_header_8"), minWidth: 100, flex: 1 },
  ]) : useMemo(() => [
    { field: PARTS_TABLE_ACCESSOR_KEYS.SUPPLIER_NUM, headerName: t("parts_tbl_column_header_1"), description: t("parts_tbl_column_header_1"), minWidth: 100, flex: 1 },
    { field: PARTS_TABLE_ACCESSOR_KEYS.DEERE_PART_NUM, headerName: t("parts_tbl_column_header_2"), description: t("parts_tbl_column_header_2"), minWidth: 100, flex: 1 },
    { field: PARTS_TABLE_ACCESSOR_KEYS.DEERE_PART_DESC, headerName: t("parts_tbl_column_header_3"), description: t("parts_tbl_column_header_3"), minWidth: 100, flex: 1 },
  ]);
  if (code !== EWDC) {
    defaultColumns.forEach((dc) => {
      if (dc.defaultColumnMapping && dc.defaultColumnMapping !== "" && campaign.isNonDeere === dc.isNonDeere) {
        if (dc.defaultColumnMapping === addCol1)
          fixedColumns.push({ field: addCol1Accessor, headerName: getTranslation(dc, 'columnNames'), description: getTranslation(dc, 'columnNames'), minWidth: 100, flex: 1 });
        else 
          fixedColumns.push({ field: addCol2Accessor, headerName: getTranslation(dc, 'columnNames'), description: getTranslation(dc, 'columnNames'), minWidth: 100, flex: 1 });
      }
    });
  }
  columns = [...fixedColumns];

  //Get part file questions
  const mapPartQuestions = new Map();

  questions
    .filter(child => child.type === QUESTION_TYPES.FILE)
    .forEach(child => mapPartQuestions.set(child.id, child));
  
  const isPopupQuestion = (que) => {
    let isPopupQue = false;
    if (que.idQuestionDepends && que.idQuestionDepends > 0) {
      let parent = questions.find((q) => q.id === que.idQuestionDepends);
      if (POPUP_QUESTIONS_TITLES.includes(parent?.type)) {
        isPopupQue = true;
      } else if (parent?.idQuestionDepends && parent?.idQuestionDepends > 0) {
        isPopupQue = isPopupQuestion(parent);
      }
    }
    return isPopupQue;
  };

  questions.forEach((que) => {
    if (!isPopupQuestion(que)) {
      switch (que.type) {
        case QUESTION_TYPES.NUM:
        case QUESTION_TYPES.OPEN: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderTextCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.SELECT: {
          columns.push({ field: "que_" + que.id,  description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderDropDownCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.TITL_POPUP: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderPopupCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.CHEK_POPUP: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderCheckCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.CHECK: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderCheckOnlyCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.FILE: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderFileUploadCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.MULT_CNTRY: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderMultiCountryCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.SEL_CNTRY: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderCountryCell(params, que, false), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.SEL_CTY_UW: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderCountryCell(params, que, true), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.SEL_STATE: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderStateCell(params, que), minWidth: 100, flex: 1 });
        }
          break;
        case QUESTION_TYPES.MULTI_SEL: {
          columns.push({ field: "que_" + que.id, description: getTranslationHelpText(que, "text"), renderHeader: () => renderHeaderCell(que), renderCell: (params) => renderMultiSelectCell(params, que), minWidth: 100, flex: 1 });
        }
          break;  
      }
    }

  });
  columns.push({ field: PARTS_TABLE_ACCESSOR_KEYS.STATUS, headerName: t("parts_tbl_column_header_9"), description: t("parts_tbl_column_header_9"), minWidth: 100, flex: 1 });

  /***************************************API Calls to fetch Country/State data**************************************/
  useEffect(() => {
    getCountry(token)
      .then((res) => {
        if (res.length > 0) {
          res.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
          dispatch(updateCountryList(res));
        }
      })
      .catch((err) => {
        console.error(err)
      });
  }, []);

  /******************************************Render Methods**********************************************************/
  const renderHeaderCell = (que) => {
    let labelText = getTranslation(que, 'text');
    let labelLength = que.links.length > 0 ? 25 : 50;
    labelText = labelText.length >= labelLength ? labelText.slice(0, labelLength) + '...' : labelText;
    return (
      <Box sx={{ whiteSpace: "normal!important", lineHeight: 1.4, fontSize: "12px" }}>
        <Box sx={{ "& .MuiBox-root": { whiteSpace: "normal!important", lineHeight: 1.4, fontSize: "12px", fontWeight: "600" }, fontWeight: "600" }}>
          <Tooltip sx={{ "& .MuiTooltip-arrow": { display: "none" }, "& .MuiTooltip-tooltip": { backgroundColor: "#666666!important" } }} title={getTranslationHelpText(que, "text")}>
            {labelText}
          </Tooltip>
        </Box>
        <Box sx={{ flexDirection: 'column' }}>
          {
            que.links.length > 0 && (<OverlayTrigger placement="bottom" trigger="click" overlay={
              <Popover id="popover-example">
                <Popover.Content>
                  <List variant="uxf-ul">
                    {
                      que.links.map((link) => <ListItem key={link}>{<Link href={link.url} target="_blank">
                        {getTranslation(link, "text")}
                      </Link>}
                      </ListItem>)
                    }
                  </List>
                </Popover.Content>
              </Popover>}>
              <IconButton style={{marginLeft: "1rem"}} variant="uxf-icon" className="btn-uxf-icon-no-hover" >
                <LinkIcon />
              </IconButton>
            </OverlayTrigger>)
          }
        </Box>
      </Box>
    )
  }


  const renderTextCell = (params, question) => {
    let showError = checkIfError(params.row.id, question.id, false);
    
    return (
      <TextField
        error={showError}
        value={params.value}
        title={params.value}
        onChange={(event) => changeHandler(params.id, [{ fieldName: params.field, value: event.target.value }])}
        onKeyDown={(event) => {
          if (event.keyCode === 32) event.stopPropagation();
        }}
        disabled={checkDisable(params.id, params.row, question)}
        sx={{ "& .MuiInputBase-input": { fontSize: "10px" } }}
      />
    );
  };

  const renderDropDownCell = (params, question) => {
    let showError = checkIfError(params.row.id, question.id, false);
    let hoverTitle = t("lbl_dropdown_select");
    if (params.value && params.value !== "") {
      let index = question.selections.length > 0 ? question.selections.findIndex((sel) => sel.value === params.value) : -1;
      if (index > -1) hoverTitle = question.selections[index].text;
    }
    const sortedSelections = question.selections.sort((a, b) => (a.text > b.text ? 1 : -1));
    return (
      <SelectField
        error={showError}
        value={params.value}
        title={hoverTitle}
        onChange={(event) => changeHandler(params.id, [{ fieldName: params.field, value: event.target.value }], question)}
        disabled={checkDisable(params.id, params.row, question)}
        sx={{ "& .MuiInputBase-root": { fontSize: "10px" } }}
      >
        <Option value="" sx={{ fontSize: "10px" }}>
          {t("lbl_dropdown_select")}
        </Option>

        {sortedSelections.map((i) => {
          let optHover = getTranslation(i, "text");
          if (i.longDescription) {
            optHover = i.translations && i.translations.length > 0 ? i.translations[0].longDescription : i.longDescription;
          }
          let optLabel = getTranslation(i, "text");
          if (optLabel.length >= 100) {
            optLabel = `${optLabel.substring(0, 100)}...`;
          }
          return (
            <Option key={i.id} value={i.value} title={optHover} sx={{ fontSize: "10px" }}>
              {optLabel}
            </Option>

          );
        })}
      </SelectField>
    );
  };

  const renderPopupCell = (params, que) => {
    let showError = checkIfError(params.row.id, que.id, true);

    //Get children questions for the current question
    const childrenQuestions = [];
    for (let i = 0; i < que.children.length; i++) {
      for (let j = 0; j < que.children[i].children.length; j++) {
        childrenQuestions.push(que.children[i].children[j]);
      }
    }

    // Get children part file questions
    const childrenPartMap = new Map();
    const arrChildrenPartQuestions = [];
    for (const childQuestion of childrenQuestions) {
      if (mapPartQuestions.has(childQuestion)) {
        childrenPartMap.set(childQuestion, childQuestion);
        arrChildrenPartQuestions.push(childQuestion);
      }
    }

    // Get answers for the children part file questions
    const childrenPartAnswers = new Map();

    for (const childQuestion of arrChildrenPartQuestions) {
      const answer = params.row[`que_${childQuestion}`]
      if (answer && answer !== "") {
        childrenPartAnswers.set(childQuestion, answer);
      }
    }

    const trimmedValue = params.value && params.value.length > 10 ? params.value.slice(0, 10) + "..." : params.value;
    let index = que.selections.length > 0 ? que.selections.findIndex((sel) => sel.value === params.value) : -1;
    const hoverValue = index > -1 ? que.selections[index].text : params.value;

    const assigmentId = params.row.assignmentId;
    const partNumber = params.row.deerePartNumber;
    return (
      <div title={hoverValue}>
        <div style={{ fontSize: "10px" }}>
          {trimmedValue}
        </div>
        <IconButton
          onClick={() => {
            setPopupCellParams({ rowParams: params, currentQue: que });
            setShowPopupCellModal(true);
          }}
          disabled={checkDisable(params.id, params.row, que)}
        >
          <AddIcon fill={showError ? "#C21020" : "#666"} />
        </IconButton>
        {Array.from(childrenPartAnswers.values()).map((answer, index) => {
          const questionId = Array.from(childrenPartAnswers.keys())[index];
          const fileName = answer.split('/').pop();
          return (
            <React.Fragment key={index}>
              <IconButton
                title={fileName}
                aria-label="Download"
                onClick={(event) => fileDownloadHandler(event, fileName, code, campaign.id, assigmentId, partNumber)}
                disabled={checkDisable(params.row.id)}
              >
                <AttachmentIcon />
              </IconButton>

              <IconButton
                title={FILE_DELETE_ATTACHMENT}
                aria-label="Delete"
                onClick={(event) => fileDeleteHandler(event, fileName, code, campaign.id, assigmentId, partNumber, params.id, que.id, questionId)}
                disabled={checkDisable(params.row.id)}
              >
                <DeleteIcon />
              </IconButton>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderCheckCell = (params, que) => {
    let showError = checkIfError(params.row.id, que.id, true);
    const childQue = questions.find((q) => q.idQuestionDepends === que.id);
    let index = childQue && childQue.id && childQue.selections.length > 0 ? childQue.selections.findIndex((sel) => sel.value === params.row["que_" + childQue.id]) : -1;
    const hoverValue = index > -1 ? childQue.selections[index].text : params.row["que_" + childQue.id];
    return (
      <div title={hoverValue}>
        <Checkbox
          onClick={(event) => {
            if (event.target.checked === false) {
              changeHandler(params.id, [{ fieldName: params.field, value: "" }]);
            } else {
              setPopupCellParams({ rowParams: params, currentQue: que });
              setShowPopupCellModal(true);
            }
          }}
          checked={params.value}
          disabled={checkDisable(params.row.id)}
        />
        {params.value && (
          <IconButton
            onClick={() => {
              setPopupCellParams({ rowParams: params, currentQue: que });
              setShowPopupCellModal(true);
            }}
            disabled={checkDisable(params.row.id)}
          >
            <AddIcon fill={showError ? "#C21020" : "#666"} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderCheckOnlyCell = (params) => {
    return (
      <div>
        <Checkbox
          onClick={(event) => {
            changeHandler(params.id, [{ fieldName: params.field, value: event.target.checked ? "true" : "" }]);
          }}
          checked={params.value === "true"}
          disabled={checkDisable(params.row.id)}
        />
      </div>
    );
  };

  const renderFileUploadCell = (params, que) => {
    const clickHandler = () => {
      fileInputRef.current.value = "";
      fileInputRef.current[`part_${params.id}_${params.field}`].click();
    };
    return !params.value && params.value === "" ? (
      <>
        <input
          type="file"
          style={{ display: "none" }}
          ref={(ref) => {
            fileInputRef.current[`part_${params.id}_${params.field}`] = ref;
          }}
          onChange={(e) => fileUploadHandler(e, params, que)}
        />
        <IconButton onClick={clickHandler} size="small">
          <UploadIcon height={20} width={20} />
        </IconButton>
      </>
    ) : (
      <>
        <IconButton onClick={(e) => fileDownloadHandler(e, params, que)} size="small">
          <AttachmentIcon height={20} width={20} />
        </IconButton>
        <IconButton onClick={(e) => fileDeleteHandler(e, params, que)} size="small">
          <DeleteIcon height={20} width={20} />
        </IconButton>
      </>
    );
  };

  const renderMultiCountryCell = (params, que) => {
    let showError = checkIfError(params.row.id, que.id, true);
    const trimmedValue = params.value && params.value.length > 10 ? params.value.slice(0, 10) + "..." : params.value;
    return (
      <Grid container direction="column">
        <Grid item title={params.value} sx={{ fontSize: "10px" }}>
          {trimmedValue}
        </Grid>
        <Grid item>
          <IconButton
            disabled={checkDisable(params.id, params.row, que)}
            onClick={() => {
              setPopupCellParams({ rowParams: params, currentQue: que });
              setShowModalMultiCountry(true);
            }}
          >
            <AddIcon fill={showError ? "#C21020" : "#666"} />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const renderCountryCell = (params, question, includeUnknown) => {
    let showError = checkIfError(params.row.id, question.id, false);
    const countryListInfo = includeUnknown ? countryListWithUnknown : countryList;
    return (
      <SelectField
        error={showError}
        value={params.value}
        title={params.value}
        onChange={(event) => changeHandler(params.id, [{ fieldName: params.field, value: event.target.value }], question)}
        disabled={checkDisable(params.id, params.row, question)}
        sx={{ "& .MuiInputBase-root": { fontSize: "10px" } }}
      >
        {countryListInfo && countryListInfo.length > 0 ? (
          <Option value="">{t("lbl_dropdown_select")}</Option>
        ) : (
          <Option disabled value="">
            {t("country_list_unavailable")}
          </Option>
        )}
        {countryListInfo &&
          countryListInfo.length > 0 &&
          countryListInfo.map((opt) => (
            <Option key={opt.iso2CountryCode} value={opt.name}>
              {opt.name}
            </Option>
          ))}
      </SelectField>
    );
  };

  const renderStateCell = (params, question) => {
    let showError = checkIfError(params.row.id, question.id, false);
    const parentQue = questions.find((q) => q.id === question.idQuestionDepends && (q.type === QUESTION_TYPES.SEL_CNTRY || q.type === QUESTION_TYPES.SEL_CTY_UW));
    const parentAns = parentQue ? params.row["que_" + parentQue.id] : null;
    const optStates = parentAns ? stateList.find((sl) => sl.country === parentAns) : [];
    return (
      <SelectField
        error={showError}
        value={params.value}
        title={params.value}
        onChange={(event) => changeHandler(params.id, [{ fieldName: params.field, value: event.target.value }], question)}
        disabled={checkDisable(params.id, params.row, question)}
        sx={{ "& .MuiInputBase-root": { fontSize: "10px" } }}
      >
        {optStates && optStates.states && optStates.states.length > 0  ? (
          <Option value="">{t("lbl_dropdown_select")}</Option>
        ) : (
          <Option disabled value="">
            {t("not_available")}
          </Option>
        )}
        {optStates &&
          optStates.states &&
          optStates.states.length > 0 &&
          optStates.states.map((opt) => (
            <Option key={opt.name} value={opt.name}>
              {opt.name}
            </Option>
          ))}
      </SelectField>
    );
  }

  const renderMultiSelectCell = (params, question) => {
    let showError = checkIfError(params.row.id, question.id, false);
    const parentQue = questions.find((q) => q.id === question.idQuestionDepends && q.type === QUESTION_TYPES.MULTI_SEL);
    const parentAns = parentQue ? params.row["que_" + parentQue.id] : null;
    
    return (
      <Select 
        size="small"
        multiple 
        value={Array.isArray(params.value)
          ? params.value
          : (params.value === undefined || params.value === null || params.value === ""
            ? []
            : String(params.value).split(","))}
        onChange={(event) => {
          const {
            target: { value },
          } = event;
          changeHandler(params.id, [{ fieldName: params.field, value: typeof value === 'string' ? value.split(',') : value }], question)
        }}
        sx={{width: "stretch"}}
        disabled={checkDisable(params.id, params.row, question)}
      >
        {question.selections.map((selection) => (
          <MenuItem
            key={selection.code}
            value={selection.value}
          >
            {selection.text}
          </MenuItem>
        ))}
      </Select>
    );
  }

  /******************************************Data Update Method******************************************************/
 
  const changeHandler = (partId, fieldsToUpdate, selectedQuestion) => {
    questions.map(question=> {
      if (selectedQuestion && question.idQuestionDepends == selectedQuestion.id){
          const key = question.id + "-" + partId
          if (question.idSelectionDependson == fieldsToUpdate[0].value || (question.type === QUESTION_TYPES.MULTI_SEL && fieldsToUpdate[0].value.includes(question.idSelectionDependson))){
            !dependentQuestions.includes(key) && dependentQuestions.push(key)
            setDependentQuestions(dependentQuestions)
          } else {
           let index = dependentQuestions.indexOf(key) 
            if(index !== -1){
              dependentQuestions.splice(index, 1)
            }
            setDependentQuestions(dependentQuestions)
          }
      }
    })
    if (fieldsToUpdate.length === 1) {
      let que = questions.find((que) => "que_" + que.id === fieldsToUpdate[0].fieldName);
      if (que && que.type === QUESTION_TYPES.CHEK_POPUP) {
        que.children.forEach((qc) => {
          qc.children.forEach((child) => {
            fieldsToUpdate.push({ fieldName: "que_" + child, value: "" });
          });
        });
      }
    }

    if (fieldsToUpdate.length === 1 && selectedQuestion) {
      selectedQuestion.children.forEach((ch) => {
        const isValueMatch = Array.isArray(fieldsToUpdate[0].value)
          ? fieldsToUpdate[0].value.includes(ch.selectionDependson.toString())
          : ch.selectionDependson.toString() === fieldsToUpdate[0].value;
        
        if (!isValueMatch) {
          ch.children.forEach((c) => {
            fieldsToUpdate.push({ fieldName: `que_${c}`, value: "" });
          });
        }
      });
    }
    
    setData((prevState) => {
      let index = prevState.findIndex((item) => item.id === partId);
      if (index > -1) {
        fieldsToUpdate.forEach((field) => (prevState[index][field.fieldName] = field.value));
      }
      return [...prevState];
    });

    updateTxnDataOnValueChange(partId, fieldsToUpdate);
    let iDsGettingUpdated = fieldsToUpdate.map((ftu) => ftu.fieldName.split("_")[1]);
    setPartErrorIds((prevState) => {
      prevState.forEach((ps) => {
        if (ps.partId == partId) {
          for (let i = ps.errQueIds.length - 1; i >= 0; i--) {
            if (iDsGettingUpdated.includes(ps.errQueIds[i].toString())) {
              ps.errQueIds.splice(i, 1);
            }
          }
        }
      });
      return [...prevState];
    });
    if (selectedQuestion && (selectedQuestion.type === QUESTION_TYPES.SEL_CNTRY || selectedQuestion.type === QUESTION_TYPES.SEL_CTY_UW)) {
      const stateIndex = stateList.findIndex((sl) => sl.country === fieldsToUpdate[0].value);
      if(stateIndex === -1) getStateList(fieldsToUpdate[0].value, selectedQuestion.type === QUESTION_TYPES.SEL_CTY_UW ? true : false);
    }
  };

  const fileUploadHandler = (event, params, que, isPopup) => {
    event.preventDefault();
    setPartsError({show: false, message: ""});
    const fileName = event.target.files[0].name;
    const extension = fileName.split(".")[fileName.split(".").length - 1];
    if (FILE_EXTENSIONS_TO_EXCLUDE.includes(extension)) {
      setPartsError({show: true, message: ERR_UNSUPPORTED_FILE});
      event.target.value = null;
    } else {
      uploadSurveyByPartAttachments(event.target.files, code, campaign.id, params.row.assignmentId, params.row.id, que.id, token, isAuthMethodOkta)
        .then((res) => res.json()).then((data) => {
          event.target.value = null;
          if (!isPopup)
            changeHandler(params.id, [{ fieldName: params.field, value: fileName }]);
        })
        .catch((err) => {
          console.log(err);
          event.target.value = null;
          setPartsError({show: true, message: ERR_UNEXPECTED_ERROR_FILE});
        });
    }
  };

  /******************************************Logic to disable rows***************************************************/

  const getRowsToBeDisabled = (data) => {
    let tempArr = [];
    data.forEach((d) => {
      if (d.statusResponse.trim().toUpperCase() === SURVEY_RESPONSE_STATUSES.SUBMITTED.toUpperCase() || d.statusResponse.trim().toUpperCase() === SURVEY_RESPONSE_STATUSES.IN_PROCESS.toUpperCase()) {
        if (selectedResponseId) {
          let responseFound = d.partAnswers.find((ans) => ans.responseId === selectedResponseId);
          if (!responseFound) tempArr.push(d.id);
        } else {
          tempArr.push(d.id);
        }
      }
    });
    return tempArr;
  };

  // Walks up the full dependency chain; returns true (disabled) if any link is not satisfied
  const checkIsDisableByDependency = (rowData, question) => {
    let current = question;
    const visitedQuestionIds = new Set();
    while (current.idQuestionDepends && current.idQuestionDepends !== 0) {
      // Fail closed on cyclic dependency chains to avoid render hangs.
      if (visitedQuestionIds.has(current.id)) return true;
      visitedQuestionIds.add(current.id);

      const parentQue = questions.find((que) => que.id === current.idQuestionDepends);
      if (!parentQue) return true;
      const parentAns = rowData[`que_${parentQue.id}`];
      if (parentQue.type === QUESTION_TYPES.CHECK) {
        const selectionDependsOn_Check = current.idSelectionDependson.toString() === "0" ? "true" : "";
        const parentAns_Check = !parentAns || parentAns === "" ? "" : parentAns;
        if (parentAns_Check !== selectionDependsOn_Check) return true;
      } else if (parentQue.type === QUESTION_TYPES.MULTI_SEL) {
        if (!parentAns || !parentAns.includes(current.idSelectionDependson.toString())) return true;
      } else {
        if (current.idSelectionDependson === 0) {
          if (!parentAns || parentAns === "") return true;
        } else {
          if (!parentAns || parentAns !== current.idSelectionDependson.toString()) return true;
        }
      }
      current = parentQue;
    }
    return false;
  };

  const checkDisable = (id, rowData, question) => {
    let isDisabled = false;
    if (excelUploading) isDisabled = true;
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index > -1) isDisabled = true;
    if (!isDisabled && rowData && question && question.idQuestionDepends && question.idQuestionDepends !== 0) isDisabled = checkIsDisableByDependency(rowData, question);
    return isDisabled;
  };

  const disableRow = (id) => {
    if (excelUploading) return true
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index === -1) return true;
    return false;
  };

  const setDisableRowStyle = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index > -1) return "disable-row-status";
    return "";
  };

  /******************************************API Call to get Parts Data**********************************************/
  useEffect(() => {
    if (Array.isArray(assignments) && assignments.length > 0) {
      const assgnIds = assignments.map((a) => a.id);
      getSurveyPartsBySupplier(assgnIds, campaign.id, token, isAuthMethodOkta)
        .then((response) => {
          if (Array.isArray(response)) {
            if (campaign.isNonDeere) {
              response = response.filter(res => res.assignmentId === campaign.assignments[0].id)
            }
            response.sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
            response.forEach((res) => {
              questionIds.forEach((id) => (res[id] = ""));
              if (res.partAnswers.length > 0) {
                
                res.partAnswers.forEach((ans) => (res["que_" + ans.questionId] = ans.answer));
              }
            });
            if (!saveButtonClickFlag) {
              fetchPartsRespHistory(response)
                .then((newResp) => {
                  updateRespWithTxnData(newResp);
                  const arrRowsToDisable = getRowsToBeDisabled(newResp);
                  const arrActive = newResp.filter((item) => !arrRowsToDisable.includes(item.id));
                  const arrInactive = newResp.filter((item) => arrRowsToDisable.includes(item.id));
                  setupTxnPartsArray(arrActive);
                  setRowsToDisable(arrRowsToDisable);
                  if (multiplePartSections) {
                    setData([...arrActive.filter(res => res.surveySectionCode === sectionCode), ...arrInactive.filter(res => res.surveySectionCode === sectionCode)]);
                  } else {
                    setData([...arrActive, ...arrInactive]);
                  }
                })
                .catch((err) => console.error(err));
            } else {
              updateRespWithTxnData(response);
              const arrRowsToDisable = getRowsToBeDisabled(response);
              const arrActive = response.filter((item) => !arrRowsToDisable.includes(item.id));
              const arrInactive = response.filter((item) => arrRowsToDisable.includes(item.id));
              setupTxnPartsArray(arrActive);
              setRowsToDisable(arrRowsToDisable);
              if (multiplePartSections) {
                setData([...arrActive.filter(res => res.surveySectionCode === sectionCode), ...arrInactive.filter(res => res.surveySectionCode === sectionCode)]);
              } else {
                setData([...arrActive, ...arrInactive]);
              }
            }
          }
          setSaveButtonClickFlag(false);
        })
        .catch((error) => {
          throw error;
        });
    } else {
      setData(undefined);
    }
  }, [token, campaign.id, assignments]);

  /***********************************Fetching history data if flag is enabled***************************************/
  const fetchPartsRespHistory = async (partResp) => {
    if (campaign.fetchHistoricalData) {
      const partRespWithHist = [...partResp];
      let dataArray = [];
      let supplierName = campaign.assignments[0].supplierName.substring(13);
      suppliers.forEach((supplier) => {
        let part = campaign.isNonDeere ? { parts: [], supplierName: supplierName, supplierNumber: "" } : { parts: [], supplierNumber: "" };
        partRespWithHist.forEach((res) => {
          if (res.supplierNumber === supplier) {
            part.parts?.push(res.deerePartNumber);
          }
        });
        part.supplierNumber = supplier;
        dataArray.push(part);
      });
      try {
        const historyResp = await getSurveyPartsHistoryBySupplier(suppliers, code, campaign.id, token, isAuthMethodOkta, campaign.isNonDeere, dataArray);
        partRespWithHist.forEach((resp) => {
          const historicalData = historyResp?.filter((data) => data.supplierNumber === resp.supplierNumber && data.partNumber === resp.deerePartNumber);
          historicalData?.forEach((data) => {
            resp[`que_${data.questionId}`] = data.answer;
          });
        });
        return partRespWithHist;
      } catch (err) {
        console.error(err);
        return partResp;
      }
    } else {
      return partResp;
    }
  };

  /*****************************************Transaction array for Parts**********************************************/
  /*Using this to maintain a component level array for parts. So that if the API call is made again, this array will
  help in maintaining the user populated data*/

  const setupTxnPartsArray = (arrParts) => {
    let tempArr = [];
    arrParts.forEach((part) => {
      for (const key in part) {
        if (key.split("_")[0] === "que" && part[key] && part[key] !== "") {
          tempArr.push({ partId: part.id, questionId: key.split("_")[1], answer: part[key] });
        }
      }
    });
    updatePartsArray(tempArr);
    setTransactionParts(tempArr);
  };

  const updateTxnDataOnValueChange = (partId, data) => {
    let tempArr = [...transactionParts];
    data.forEach((d) => {
      let index = tempArr.findIndex((p) => d.fieldName === "que_" + p.questionId && partId === p.partId);
      if (index > -1 && d.value !== "") {
        tempArr[index].answer = Array.isArray(d.value) ? d.value.join(",") : d.value;
      } else if (index === -1 && d.value !== "") {
        tempArr.push({ partId: partId, questionId: d.fieldName.split("_")[1], answer: d.value });
      } else if (index > -1 && d.value === "") {
        tempArr.splice(index, 1);
      }
    });
    updatePartsArray(tempArr);
    setTransactionParts(tempArr);
  };

  const updateRespWithTxnData = (data) => {
    data.forEach((d) => {
      let foundInTxn = transactionParts && Array.isArray(transactionParts) ? transactionParts.filter((txn) => txn.partId === d.id) : [];
      foundInTxn.forEach((txn) => {
        d["que_" + txn.questionId] = txn.answer;
      });
    });
  };

  /*************************Checking if the input cell should be highlighted for errors******************************/

  const checkIfError = (partId, quesId, isPopupTitle) => {
    let index = partErrorIds.findIndex((err) => err.partId == partId);
    if (index === -1) return false;
    else {
      let errorIds = partErrorIds[index].errQueIds;
      let queIdx = errorIds.findIndex((qId) => qId == quesId);
      if (queIdx !== -1) return true;
      else if (isPopupTitle) {
        let childQues = questions.find((que) => que.id == quesId).children;
        let quesInPopup = [];
        childQues.forEach((cq) => (quesInPopup = quesInPopup.concat(cq.children)));
        for (let i = 0; i < quesInPopup.length; i++) {
          if (errorIds.includes(quesInPopup[i])) {
            return true;
          }
        }
      } else {
        return false;
      }
    }
  };

  //Download file from s3Bucket
  const fileDownloadHandler = (e, params, que) => {
    e.preventDefault();
    setPartsError({show: false, message: ""});
    getSurveyByPartAttachmentUrl(params.value, code, campaign.id, params.row.assignmentId, params.row.id, que.id, token, isAuthMethodOkta)
      .then(async (response) => {
        if (response.status === 200) {
          const arrayBuffer = await response.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: response.headers.get('Content-Type') || 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", params.value);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          setPartsError({show: true, message: ERR_FILE_NOT_AVAILABLE});
          console.error(`Error: ${response.statusText}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  //Delete file handler
  const fileDeleteHandler = (e, params, que) => {
    e.preventDefault();
    setPartsError({show: false, message: ""});
    deleteSurveyByPartAttachment(params.value, code, campaign.id, params.row.assignmentId, params.row.id, que.id, token, isAuthMethodOkta)
      .then((res) => {
        if (res.message !== FILE_DELETE_RESPONSE) {
          setPartsError({show: true, message: ERR_FILE_DELETION});
          console.warn(res);
        } else {
          const fieldsToUpdate = [{ fieldName: "que_" + que.id, value: "" }];
          changeHandler(params.row.id, fieldsToUpdate);
        }
      })
      .catch((err) => {
        setPartsError({show: true, message: ERR_FILE_DELETION});
        console.error(err);
      });
  };

  /***********************************API Call to fetch State List***********************************/
  const getStateList = (selectedCountry, includeUnknown) => {
    const countryListInfo = includeUnknown ? countryListWithUnknown : countryList;
    const countryCode = countryListInfo.find((c) => c.name === selectedCountry);
    if (countryCode && countryCode.iso2CountryCode) {
      getBoundary(token, countryCode.iso2CountryCode)
        .then((res) => {
          if (Array.isArray(res)) {
            res.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
            dispatch(updateStateByQuesIdList({country: selectedCountry, states: res}));
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const CustomToolbar = () => {
    return (
      <Grid container justifyContent="space-between" marginBottom={2}>
        <Grid item>
          <GridToolbarFilterButton />
        </Grid>
        <Grid item>
          <Grid item>
            <Grid item style={{ padding: "2px", fontSize: "12px" }}>
              {t('download_upload_lbl')}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item>
              <DownloadPartsTable data={data} fixedColumns={fixedColumns} questions={questions} rowsToDisable={rowsToDisable} sampleData={sampleData} surveyCode={code} />
            </Grid>
            <Grid item>
              <UploadPartsTable
                data={data}
                fixedColumns={fixedColumns}
                questions={questions}
                rowsToDisable={rowsToDisable}
                setData={setData}
                updatePartsArray={updatePartsArray}
                setTransactionParts={setTransactionParts}
                transactionParts={transactionParts}
                setPartErrorIds={setPartErrorIds}
                sampleData={sampleData}
                isAuthMethodOkta={isAuthMethodOkta}
                token={token}
                setExcelUploading={setExcelUploading}
                showWarningModal={showWarningModal}
                setShowWarningModal={setShowWarningModal}
                activeDataForUpload={activeDataForUpload}
                inactiveDataForWarning={inactiveDataForWarning}
                setActiveDataForUpload={setActiveDataForUpload}
                setInactiveDataForWarning={setInactiveDataForWarning}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const dTable = (
    <Box sx={style}>
      <DataGrid
        rows={data}
        columns={columns}
        autoHeight
        pageSize={5}
        disableSelectionOnClick
        disableColumnMenu
        isRowSelectable={(params) => disableRow(params.row.id)}
        getRowClassName={(params) => setDisableRowStyle(params.row.id)}
        onSelectionModelChange={(ids) => setSelectionModel(ids)}
        selectionModel={selectionModel}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal!important", lineHeight: 1.4, fontSize: "12px" },
          "& .MuiDataGrid-cell:focus-within": { outline: "none !important" },
          "& .MuiDataGrid-cellContent": { fontSize: "10px" },
          "& .MuiDataGrid-cell": { padding: "0 5px" },
        }}
        components={{ Toolbar: CustomToolbar }}
        headerHeight={110}
        loading={excelUploading}
      />
    </Box>
  );

  return Array.isArray(data) ? (
    <Grid container>
      <Grid item xs={12}>
        {dTable}
        {partsError.show && (
          <Typography variant="subtitle1" color="#C21020" textAlign="right">
            {partsError.message}
          </Typography>
        )}
      </Grid>
      {showPopupCellModal && (
        <ModalCellPopup
          showModal={showPopupCellModal}
          setShowModal={setShowPopupCellModal}
          popupCellParams={popupCellParams}
          saveHandler={changeHandler}
          questions={questions}
          checkIfError={checkIfError}
          token={token}
          isAuthMethodOkta={isAuthMethodOkta}
          surveyCode={code}
          campaignId={campaign.id}
          fileUploadHandler={fileUploadHandler}
          fileDownloadHandler={fileDownloadHandler}
        />
      )}
      <ModalMultiCountry
        show={showModalMultiCountry}
        handleClose={() => setShowModalMultiCountry(false)}
        popupCellParams={popupCellParams}
        changeHandler={changeHandler}
      />
    </Grid>
  ) : (
    <CircleProgressIndicator title={t("tps_lbl_waiting_country_message_title")} subtitle={t("tps_lbl_waiting_country_message_subtitle")} />
  );
};
